// Types
import type {
  ContinueBookingBody,
  SagaSuccessfulStatusResponse,
} from '@white-label-helper/api-parkings-bookings';
import type { Channel, default as Pusher } from 'pusher-js';

// Constants
import { pusherErrorTimeout } from '@white-label-configuration/constants';

// Helpers
import {
  continueBookingRequest,
  checkSagaStatusOnTimeout,
  getTimeoutErrorMessage,
} from '@white-label-helper/api-parkings-bookings';
import getPusherChannel from '@white-label-helper/pusher-channel';
import { addCustomMetric } from '@white-label-helper/shared-helpers';

type PusherEvent = {
  management_token: {
    token: string,
    expiration: number
  },
  order_reference: string;
}

let pusher: Pusher;

function handlePaymentError(error: Error, startTime: number, reject: (reason?: Error) => void, timeoutId?: NodeJS.Timeout) {
  clearTimeout(timeoutId);

  addCustomMetric("error", "Booking Metric: Exception", {
    failure_response: error instanceof Error ? JSON.stringify(error.message) : JSON.stringify(error),
    event: 'BookingFailed'
  }, startTime, 'booking_service');

  reject(error);
}

export function continueBookingHelper(flowKey: 'original' | 'manageBooking' | 'manageExtendBooking') {
  let successEventName: string;
  let failedEventName: string;

  switch (flowKey) {
    case 'original': {
      successEventName = 'OrderCompletedEvent';
      failedEventName = 'OrderCreatingFailedEvent';
      break;
    }
    case 'manageBooking': {
      successEventName = 'OrderAmendmentCompletedEvent';
      failedEventName = 'OrderUpdatingFailedEvent';
      break;
    }
    case 'manageExtendBooking': {
      successEventName = 'OrderUpgradeCompletedEvent';
      failedEventName = 'OrderUpdatingFailedEvent';
      break;
    }
  }

  let channelName: string;
  let timeoutId: NodeJS.Timeout;

  function destroyContinueBooking() {
    if (pusher === undefined) {
      pusher = getPusherChannel();
    }

    pusher.unsubscribe(channelName);
  }

  /**
   * Handles the continuation of the booking process via Pusher events.
   * Falls back to polling the saga status endpoint on Pusher timeout.
   * @param {ContinueBookingBody} body - The payload to send in the booking request.
   * @param {Channel} channel - The Pusher channel used for listening to booking events.
   * @returns {Promise<PusherEvent | SagaSuccessfulStatusResponse>} A promise that resolves with the success event data,
   * or rejects with an error if the booking fails, encounters an error, or times out without a successful status.
   */
  function continueBooking(body: ContinueBookingBody, channel: Channel) {
    const startTime = performance.now();
    const sagaId = body.forward?.saga_id;

    return new Promise<PusherEvent | SagaSuccessfulStatusResponse>((resolve, reject) => {
      channel.bind(successEventName, (data: PusherEvent) => {
        clearTimeout(timeoutId);
        addCustomMetric("tracking", "Booking Metric: Success", {
          event: 'BookingSuccess'
        }, startTime, 'booking_service');
        resolve(data);
      });

      channel.bind(failedEventName, (error: Error) => {
        handlePaymentError(error, startTime, reject, timeoutId);
      });

      channel.bind('error', (error: Error) => {
        handlePaymentError(error, startTime, reject, timeoutId);
      });

      continueBookingRequest(body).catch((error: Error) => {
        handlePaymentError(error, startTime, reject, timeoutId);
      });

      // Default timeout - on timeout, fall back to checking saga status
      timeoutId = setTimeout(async () => {
        if (!sagaId) {
          reject(getTimeoutErrorMessage());
          return;
        }

        try {
          const result = await checkSagaStatusOnTimeout(sagaId);
          addCustomMetric(
            'tracking',
            'Booking Metric: Success via Status Fallback',
            {
              sagaResponse: JSON.stringify(result),
            },
            startTime,
            'booking_service',
          );
          resolve(result);
        } catch (error: unknown) {
          addCustomMetric(
            'error',
            'Booking Metric: Status Fallback Error',
            {
              failure_response:
                error instanceof Error ? error.message : JSON.stringify(error),
            },
            startTime,
            'booking_service',
          );
          reject(error instanceof Error ? error : getTimeoutErrorMessage());
        }
      }, pusherErrorTimeout);
    }).catch((error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);

      addCustomMetric(
        'error',
        `pusher-continue-booking: ${errorMessage}`,
        {
          failure_response: errorMessage,
        },
        startTime,
        'booking_service',
      );
      clearTimeout(timeoutId);
      throw error instanceof Error ? error : new Error(errorMessage);
    });
  }

  return {
    continueBooking,
    destroyContinueBooking,
  };
}
